import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { WalletAdapterName } from '@/contexts/WalletContext';
import {
  generateBackpackInAppBrowserUrl,
  generateSolflareInAppBrowserUrl,
  generateMagicEdenInAppBrowserUrl,
  redirectToMobileWallet
} from '@/lib/mobileWalletUtils';
import IconSolflare from '@/assets/icons/solflare.jpeg';
import IconBackpack from '@/assets/icons/backpack.png';
import IconMagicEden from '@/assets/icons/magiceden.png'; // TODO: Add Magic Eden icon
import IconCross from '@/assets/icons/cross.svg';

type ModalContext = 'connect' | 'setPrimary' | 'mint' | 'upgrade' | 'claim';

interface MobileWalletNavigationModalProps {
  visible: boolean;
  walletType: 'Solflare' | 'Backpack' | 'MagicEden' | null;
  context: ModalContext | null;
  onClose: () => void;
}

const MobileWalletNavigationModal: React.FC<MobileWalletNavigationModalProps> = ({
  visible,
  walletType,
  context,
  onClose,
}) => {
  if (!walletType || !context) return null;

  const getWalletIcon = () => {
    const walletLower = walletType?.toLowerCase();
    if (walletLower === 'solflare') return IconSolflare;
    if (walletLower === 'backpack') return IconBackpack;
    if (walletLower === 'magiceden') return IconMagicEden; // TODO: Uncomment when icon is added
    return IconBackpack; // Fallback for Magic Eden until icon is added
  };

  const getDescription = () => {
    switch (context) {
      case 'connect':
        return `You have a ${walletType} primary wallet. Open ${walletType} in-app browser to connect automatically.`;
      case 'setPrimary':
        return `Primary wallet set to ${walletType}. Open ${walletType} in-app browser to use this wallet, or change your primary wallet.`;
      case 'mint':
        return `Your primary wallet is ${walletType}. Open ${walletType} in-app browser to mint, or change your primary wallet.`;
      default:
        return '';
    }
  };

  const handleOpenWallet = () => {
    const walletLower = walletType?.toLowerCase();
    let url: string;

    if (walletLower === 'solflare') {
      url = generateSolflareInAppBrowserUrl();
    } else if (walletLower === 'backpack') {
      url = generateBackpackInAppBrowserUrl();
    } else if (walletLower === 'magiceden') {
      url = generateMagicEdenInAppBrowserUrl();
    } else {
      url = generateBackpackInAppBrowserUrl(); // Fallback
    }

    redirectToMobileWallet(url);
    onClose();
  };

  const walletIcon = getWalletIcon();

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] bg-backgroundGray border-[#2A2A2A]" shouldShowCloseButton={false}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        >
          <img src={IconCross} alt="Close" className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        <DialogHeader>
          <div className="flex flex-col items-center mb-4">
            <img
              src={walletIcon}
              alt={walletType}
              className="w-16 h-16 rounded-lg mb-4"
            />
            <DialogTitle className="text-white text-xl font-inter text-center">
              Open {walletType} In-App Browser
            </DialogTitle>
          </div>
          <DialogDescription className="text-tertiary font-inter text-center">
            {getDescription()}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 mt-4">
          <button
            onClick={handleOpenWallet}
            className="w-full py-3 px-4 rounded-lg bg-black hover:bg-[#1A1A1A] border border-[#2A2A2A] text-white font-inter font-medium transition-colors"
          >
            Open {walletType}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MobileWalletNavigationModal;
